import {
  AirportAssistanceContent,
  BaggageContent,
  FooterContent,
  LoungeContent,
  PriceDisclaimerContent,
} from './_interfaces_/content.interface';
import {
  AirportAssistanceContentMapped,
  AlertMessageContentMapped,
  AssetMapped,
  BaggageContentMapped,
  FooterContentMapped,
  LoungeContentMapped,
  PriceDisclaimerContentMapped,
} from '../content/_interfaces_/content.mapped.interface';
import { INLINES, Inline } from '@contentful/rich-text-types';
import { Alert } from '@pages/common-order/_interfaces_/order.interface';
import { AncillaryServicePrice } from '../../../pages/common-order/_interfaces_/shopping-cart.interface';
import { Asset } from 'contentful';
import { FinalizeOrderContent } from './_interfaces_/finalize-order-content.interface';
import { FlightAlert } from '@pages/common-order/_interfaces_/flight-alert.interface';
import { Injectable } from '@angular/core';
import { ShoppingCartContent } from './_interfaces_/content-order-page.interface';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { Document } from '@contentful/rich-text-types';
import { get } from 'lodash';

@Injectable({
  providedIn: 'root',
})
export class ContentMapperService {
  constructor() {}

  mapPriceDisclaimer(content: PriceDisclaimerContent): PriceDisclaimerContentMapped {
    const options = {
      renderNode: {
        [INLINES.ASSET_HYPERLINK]: this.mapInlineHyperlink,
      },
    };

    return {
      label: documentToHtmlString(content.label, options),
    };
  }

  mapAlertMessage(content: Document): AlertMessageContentMapped {
    const options = {
      renderNode: {
        [INLINES.HYPERLINK]: (node: any) => {
          const uri = get(node, 'data.uri', null);
          if (uri) {
            return `<a href="${uri}" target="_blank" rel="noopener noreferrer">${node?.content[0]?.value}</a>`;
          }
          return `<span>${content}</span>`;
        },
      },
    };

    return {
      body: documentToHtmlString(content, options),
    };
  }

  mapInlineHyperlink = (node: Inline): string => {
    const assetLink = get(node, 'data.target.fields.file.url', null);
    const text = get(node, 'content[0].value', null);

    return assetLink && text ? `<a target="_blank" rel="noopener noreferrer" href="${assetLink}">${text}</a>` : '';
    // tslint:disable-next-line: semicolon
  };

  mapFooterContent(content: FooterContent): FooterContentMapped {
    return {
      anvrLogo: this.mapAsset(content.anvrLogo),
      companyLogo: this.mapAsset(content.companyLogo),
      iataLogo: this.mapAsset(content.iataLogo),
      sgrLogo: this.mapAsset(content.sgrLogo),
      links: content.links?.length ? content.links.map((link) => this.mapAsset(link)) : null,
    };
  }

  mapBaggageContent(content: BaggageContent): BaggageContentMapped {
    const extraBaggageFields = content.extraBagage.fields;
    return {
      baggageTitle: extraBaggageFields.bagageTitle,
      selectBagage: extraBaggageFields.selectBagage,
      baggageExtra: extraBaggageFields.bagageExtra,
      dropdownPlaceholder: extraBaggageFields.dropdownPlaceholder,
      baggagePiece: extraBaggageFields.bagagePiece,
      baggageTotal: extraBaggageFields.bagageTotal,
      baggageIcon: this.mapAsset(extraBaggageFields.bagageIcon),
      extras: extraBaggageFields.extras,
      status: extraBaggageFields.status,
    };
  }

  mapLoungeContent(content: LoungeContent): LoungeContentMapped {
    const extraLoungeFields = content.extraLounge.fields;
    return {
      loungeTitle: extraLoungeFields.loungeTitle,
      loungeIcon: this.mapAsset(extraLoungeFields.loungeIcon),
      add: extraLoungeFields.add,
      remove: extraLoungeFields.remove,
      flightTo: extraLoungeFields.flightTo,
      at: extraLoungeFields.at,
      selected: extraLoungeFields.selected,
      total: extraLoungeFields.total,
      removing: extraLoungeFields.removing,
      selecting: extraLoungeFields.selecting,
    };
  }

  mapAirportAssistanceContent(content: AirportAssistanceContent): AirportAssistanceContentMapped {
    const extraAirportAssistanceFields = content.extraAirportAssistance?.fields;
    return {
      airportAssistanceTitle: extraAirportAssistanceFields.airportAssistanceTitle,
      airportAssistanceSelect: extraAirportAssistanceFields.airportAssistanceSelect,
      airportAssistanceCostless: extraAirportAssistanceFields.airportAssistanceCostless,
      BLND: extraAirportAssistanceFields.BLND,
      DEAF: extraAirportAssistanceFields.DEAF,
      WCHR: extraAirportAssistanceFields.WCHR,
      WCHS: extraAirportAssistanceFields.WCHS,
      airportAssistanceIcon: this.mapAsset(extraAirportAssistanceFields.airportAssistanceIcon),
      airportAssistancePassengerIcon: this.mapAsset(extraAirportAssistanceFields.airportAssistancePassengerIcon),
      selectionSummary: extraAirportAssistanceFields.selectionSummary,
    };
  }

  public mapAlerts(content: FinalizeOrderContent, alerts: Alert[]): FlightAlert[] {
    if (!alerts?.length) {
      return [];
    }

    const contentfulKeyMap: Record<string, string> = Object.keys(content).reduce((acc, key) => {
      acc[key.toLowerCase()] = key;
      return acc;
    }, {} as Record<string, string>);

    const contentMapped = alerts.map((alert: any) => {
      const alertTypeLower = alert.alertType.toLowerCase();
      const matchedKey = contentfulKeyMap[alertTypeLower];
      const contentfulFields = matchedKey ? content[matchedKey]?.fields ?? {} : {};

      return {
        ...alert,
        ...contentfulFields,
      };
    });

    return contentMapped;
  }

  public mapAncillaryProducts(
    shoppingCartContent: ShoppingCartContent,
    ancillaryServices: AncillaryServicePrice[]
  ): AncillaryServicePrice[] {
    if (ancillaryServices?.length) {
      const contentKeyMap = Object.keys(shoppingCartContent).reduce((map, key) => {
        map[key.toLowerCase()] = key;
        return map;
      }, {} as Record<string, string>);

      return ancillaryServices.map((service: AncillaryServicePrice) => {
        const subpPoductTypeLower: string = service.subproductType?.toLowerCase();
        const originalKey: string = subpPoductTypeLower ? contentKeyMap[subpPoductTypeLower] : null;
        const contentLabel = originalKey ? shoppingCartContent[originalKey] : null;

        return {
          ...service,
          displayName: contentLabel || service.productName,
        };
      });
    }

    return [];
  }

  mapAsset(asset: Asset): AssetMapped {
    if (!asset) {
      return null;
    }

    return {
      title: asset.fields?.title || '',
      description: asset.fields?.description || '',
      url: asset.fields?.file.url,
    };
  }
}
